import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { usePool, useDeletePool } from '@/hooks/use-pools'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  ArrowLeft,
  Loader2,
  Info,
  List,
  Zap,
  Activity,
  Copy,
  Check,
  TestTube,
  Clock,
  Crown,
  Pencil,
  Trash2,
  Settings2,
  TrendingUp,
  Car,
} from 'lucide-react'
import { getPoolIcon, forecastPurposeLabel } from '@/lib/entity-icons'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { EditPoolDialog } from '@/components/pools/EditPoolDialog'
import EnableTouDialog from '@/components/pools/EnableTouDialog'
import { formatDate } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'
import { useIncognito } from '@/hooks/use-incognito'
import { AuthorizedButton } from '@/components/ui/AuthorizedButton'
import { usePermissions } from '@/hooks/use-permissions'
import { obfuscateTags } from '@/lib/incognito'
import { useAccount } from '@/hooks/use-accounts'
import SystemList from '@/components/systems/SystemList'
import PoolHistoryView from '@/components/systems/PoolHistoryView'
import ControlSignalForm from '@/components/control/ControlSignalForm'
import SignalTimeline from '@/components/control/SignalTimeline'
import PIDControllerMonitor from '@/components/monitoring/PIDControllerMonitor'
import MonitoringDashboard from '@/components/monitoring/MonitoringDashboard'
import { EVConfigView } from '@/components/monitoring/EVConfigView'

export default function PoolDetailPage() {
  const { poolID, tab } = useParams<{ poolID: string; tab: string }>()
  const navigate = useNavigate()
  const { data: selectedPool, isLoading: loading } = usePool(poolID ?? '')
  const incognito = useIncognito()
  const { data: account } = useAccount(selectedPool?.accountID ?? '')
  const activeTab = tab ?? 'overview'
  const { mutateAsync: deletePool } = useDeletePool()
  const { can } = usePermissions()
  const [copied, setCopied] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [enableTouDialogOpen, setEnableTouDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [deleting, setDeleting] = useState(false)
  const { toast } = useToast()

  const handleCopyPoolId = async () => {
    if (!selectedPool) return

    try {
      await navigator.clipboard.writeText(selectedPool.poolID)
      setCopied(true)
      toast({
        title: 'Copied',
        description: 'Pool ID copied to clipboard',
      })

      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to copy to clipboard',
        variant: 'destructive',
      })
    }
  }

  const handleDeleteConfirm = async () => {
    if (!selectedPool) return
    const requiredText = incognito.isIncognito ? 'DELETE' : selectedPool.name
    if (deleteConfirmText !== requiredText) return
    setDeleting(true)
    try {
      await deletePool(selectedPool.poolID)
      navigate('/', { replace: true })
      toast({ title: 'Success', description: `Pool "${selectedPool.name}" deleted successfully` })
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'Failed to delete pool', variant: 'destructive' })
    } finally {
      setDeleting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!selectedPool) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/pools')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Pools
        </Button>
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Pool not found</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/pools')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <div>
            <div className="flex items-center gap-3">
              {(() => {
                const PoolIcon = getPoolIcon(selectedPool)
                return <PoolIcon className="h-8 w-8 text-muted-foreground" />
              })()}
              <h1 className="text-3xl font-bold tracking-tight">
                {incognito.poolName(selectedPool.name)}
              </h1>
              {(selectedPool as any).controlConfig?.dryRun && (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-sm font-medium text-amber-900 border border-amber-200"
                  title="Dry Run Mode - Commands are logged but not executed"
                >
                  <TestTube className="h-4 w-4" />
                  Dry Run
                </span>
              )}
              {(selectedPool as any).isDefault && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-900 border border-emerald-200">
                  <Crown className="h-4 w-4" />
                  Default
                </span>
              )}
              {(selectedPool as any).forecastConfigs?.map((f: { purpose: string; isActive: boolean }) => (
                <span
                  key={f.purpose}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium border ${
                    f.isActive
                      ? 'bg-blue-50 text-blue-900 border-blue-200'
                      : 'bg-muted text-muted-foreground border-border'
                  }`}
                >
                  <TrendingUp className="h-4 w-4" />
                  {forecastPurposeLabel(f.purpose)}
                  {!f.isActive && <span className="text-xs font-normal opacity-70">(inactive)</span>}
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-muted-foreground font-mono text-sm">
                {incognito.uuid(selectedPool.poolID)}
              </p>
              {!incognito.isIncognito && (
                <button
                  onClick={handleCopyPoolId}
                  className="p-1 hover:bg-muted rounded transition-colors"
                  title="Copy Pool ID"
                >
                  {copied ? (
                    <Check className="h-3.5 w-3.5 text-green-500" />
                  ) : (
                    <Copy className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <AuthorizedButton authorized={can('PoolsWrite')} variant="outline" size="sm" onClick={() => setEditDialogOpen(true)}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit Pool
          </AuthorizedButton>
          <AuthorizedButton
            authorized={can('PoolsWrite')}
            variant="ghost"
            size="sm"
            onClick={() => { setDeleteDialogOpen(true); setDeleteConfirmText('') }}
            className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-4 w-4" />
          </AuthorizedButton>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={(value) => navigate(`/pools/${poolID}/${value}`, { replace: true })}
        className="space-y-6"
      >
        <TabsList>
          <TabsTrigger value="overview">
            <Info className="mr-2 h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="systems">
            <List className="mr-2 h-4 w-4" />
            Systems
          </TabsTrigger>
          <TabsTrigger value="control">
            <Zap className="mr-2 h-4 w-4" />
            Control
          </TabsTrigger>
          <TabsTrigger value="history">
            <Clock className="mr-2 h-4 w-4" />
            History
          </TabsTrigger>
          <TabsTrigger value="monitoring">
            <Activity className="mr-2 h-4 w-4" />
            Monitoring
          </TabsTrigger>
          {/* EV Config Tab - Only for intraday pools with EV permissions */}
          {(selectedPool as any).controlConfig?.purpose === 'intraday' &&
            can('AppliancesEVConfigurationRead') &&
            can('EVProfileRead') &&
            can('SystemMeasurementsRead') && (
            <TabsTrigger value="ev-config">
              <Car className="mr-2 h-4 w-4" />
              EV Config
            </TabsTrigger>
          )}
          {/* PID Data Tab - Only for imbalance and tou_stacking_poc pools */}
          {((selectedPool as any).controlConfig?.purpose === 'imbalance' ||
            (selectedPool as any).controlConfig?.purpose === 'tou_stacking_poc') && (
            <TabsTrigger value="pid-data">
              <Settings2 className="mr-2 h-4 w-4" />
              PID Data
            </TabsTrigger>
          )}
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Pool Information</CardTitle>
              <CardDescription>Basic pool configuration and metadata</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Account ID</p>
                  <div className="flex items-center gap-1.5 min-w-0">
                    <p className="font-mono text-sm truncate">
                      {incognito.uuid(selectedPool.accountID)}
                    </p>
                    {!incognito.isIncognito && account?.customization?.logo && (
                      <img
                        src={account.customization.logo}
                        alt=""
                        className="h-4 w-auto max-w-[48px] object-contain flex-shrink-0"
                      />
                    )}
                  </div>
                </div>
                {(selectedPool as any).controlConfig?.purpose && (
                  <div>
                    <p className="text-sm text-muted-foreground">Control Purpose</p>
                    <p className="font-medium capitalize">
                      {(selectedPool as any).controlConfig.purpose.replace(/_/g, ' ')}
                    </p>
                  </div>
                )}
                {(selectedPool as any).controlConfig?.fallbackBehavior &&
                  (selectedPool as any).controlConfig.fallbackBehavior !== 'none' && (
                    <div>
                      <p className="text-sm text-muted-foreground">Fallback Behavior</p>
                      <p className="font-medium capitalize">
                        {(selectedPool as any).controlConfig.fallbackBehavior.replace(/_/g, ' ')}
                      </p>
                    </div>
                  )}
                {(selectedPool as any).forecastConfigs?.length > 0 && (
                  <div>
                    <p className="text-sm text-muted-foreground">Forecast Configurations</p>
                    <div className="flex flex-col gap-1 mt-1">
                      {(selectedPool as any).forecastConfigs
                        .map((f: { purpose: string; isActive: boolean }) => (
                          <span key={f.purpose} className="flex items-center gap-2">
                            <span
                              className={`inline-block h-2 w-2 rounded-full shrink-0 ${
                                f.isActive ? 'bg-blue-500' : 'bg-muted-foreground/40'
                              }`}
                            />
                            <span className={`font-medium text-sm ${!f.isActive ? 'text-muted-foreground' : ''}`}>
                              {forecastPurposeLabel(f.purpose)}
                            </span>
                            {!f.isActive && (
                              <span className="text-xs text-muted-foreground">inactive</span>
                            )}
                          </span>
                        ))}
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-sm text-muted-foreground">Mode</p>
                  <p className="font-medium">
                    {(selectedPool as any).controlConfig?.dryRun ? (
                      <span className="inline-flex items-center gap-1 text-amber-700">
                        <TestTube className="h-3.5 w-3.5" />
                        Dry Run
                      </span>
                    ) : (
                      'Live'
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Created</p>
                  <p className="font-medium">{formatDate((selectedPool as any).createdAt)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Last Modified</p>
                  <p className="font-medium">{formatDate((selectedPool as any).modifiedAt)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Default Pool</p>
                  <p className="font-medium">
                    {(selectedPool as any).isDefault ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700">
                        <Crown className="h-3.5 w-3.5" />
                        Yes
                      </span>
                    ) : (
                      'No'
                    )}
                  </p>
                </div>
              </div>

              {selectedPool.tags && Object.keys(selectedPool.tags).length > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Tags</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(
                      incognito.isIncognito
                        ? obfuscateTags(selectedPool.tags as Record<string, string>)
                        : selectedPool.tags
                    ).map(([key, value]) => (
                      <span
                        key={key}
                        className="inline-flex items-center rounded-full bg-muted px-3 py-1 text-sm"
                      >
                        <span className="font-medium">{key}:</span>
                        <span className="ml-1">{value as string}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Systems Tab */}
        <TabsContent value="systems" className="space-y-6">
          <SystemList
            poolID={selectedPool.poolID}
            poolPurpose={(selectedPool as any).controlConfig?.purpose ?? null}
            touFallback={(selectedPool as any).controlConfig?.fallbackBehavior === 'time_of_use'}
            isIncognito={incognito.isIncognito}
          />
        </TabsContent>

        {/* Control Tab */}
        <TabsContent value="control" className="space-y-6">
          <SignalTimeline
            poolID={selectedPool.poolID}
            poolName={incognito.poolName(selectedPool.name)}
          />
          <ControlSignalForm
            poolID={selectedPool.poolID}
            poolName={incognito.poolName(selectedPool.name)}
          />
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-6">
          <PoolHistoryView
            poolID={selectedPool.poolID}
          />

          <Card>
            <CardHeader>
              <CardTitle>System History</CardTitle>
              <CardDescription>
                View individual system timeline by clicking the History icon next to any system in
                the Systems tab
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                💡 Tip: Navigate to the Systems tab and click the clock icon next to any system to
                view its complete history of additions and removals across pools.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Monitoring Tab — full-viewport dashboard, no page scroll */}
        <TabsContent
          value="monitoring"
          style={{ height: 'calc(100vh - 262px)' }}
          className="overflow-hidden mt-0"
        >
          <MonitoringDashboard
            poolID={selectedPool.poolID}
            poolName={incognito.poolName(selectedPool.name)}
            poolPurpose={(selectedPool as any).controlConfig?.purpose}
            forecastConfigs={(selectedPool as any).forecastConfigs}
          />
        </TabsContent>

        {/* EV Config Tab - Only for intraday pools with EV permissions */}
        {(selectedPool as any).controlConfig?.purpose === 'intraday' &&
          can('AppliancesEVConfigurationRead') &&
          can('EVProfileRead') &&
          can('SystemMeasurementsRead') && (
          <TabsContent
            value="ev-config"
            style={{ height: 'calc(100vh - 262px)' }}
            className="overflow-hidden mt-0"
          >
            <EVConfigView poolID={selectedPool.poolID} />
          </TabsContent>
        )}

        {/* PID Data Tab - Only for imbalance and tou_stacking_poc pools */}
        {((selectedPool as any).controlConfig?.purpose === 'imbalance' ||
          (selectedPool as any).controlConfig?.purpose === 'tou_stacking_poc') && (
          <TabsContent value="pid-data" className="space-y-6">
            <PIDControllerMonitor
              poolID={selectedPool.poolID}
              poolName={incognito.poolName(selectedPool.name)}
            />
          </TabsContent>
        )}
      </Tabs>

      <EditPoolDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        pool={selectedPool}
        onFallbackSetToTou={() => setEnableTouDialogOpen(true)}
      />
      <EnableTouDialog
        open={enableTouDialogOpen}
        onClose={() => setEnableTouDialogOpen(false)}
        pool={selectedPool}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => { if (!open) { setDeleteDialogOpen(false); setDeleteConfirmText('') } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Pool</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{' '}
              <strong>"{incognito.poolName(selectedPool.name)}"</strong>?
              <br />
              <br />
              This action cannot be undone. All pool configuration and history will be permanently
              removed.
              <br />
              <br />
              Type <strong>{incognito.isIncognito ? 'DELETE' : selectedPool.name}</strong> to confirm:
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-6">
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Enter pool name"
              disabled={deleting}
              autoFocus
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleting || deleteConfirmText !== (incognito.isIncognito ? 'DELETE' : selectedPool.name)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete Pool
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
